import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { StaticPyramidLogo } from '@/components/landing/StaticPyramidLogo';
import { Loader2, FileDown, Clock } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { useAuthStore } from '@/hooks/useAuthStore';
import { useSessionTimeout } from '@/hooks/useSessionTimeout';
import { logger } from '@/lib/logger';

interface NDAAgreementModalProps {
  companyId: string;
  companyName?: string;
  nda: {
    id: string;
    name: string;
    version: string;
    content_md: string;
  };
  progressInfo?: {
    current: number;
    total: number;
  };
}

export function NDAAgreementModal({
  companyId,
  companyName,
  nda,
  progressInfo,
}: NDAAgreementModalProps) {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const sessionState = useSessionTimeout();
  const [agreed, setAgreed] = useState(false);
  const [accepting, setAccepting] = useState(false);

  const handleAccept = async () => {
    if (!user || !agreed) return;

    setAccepting(true);

    try {
      // Call server-side edge function for secure IP capture and rate limiting
      const { data, error } = await supabase.functions.invoke('nda-accept', {
        body: {
          companyId,
          ndaId: nda.id,
        },
      });

      if (error) throw error;

      if (!data.success) {
        throw new Error(data.error || 'Failed to accept NDA');
      }

      logger.audit('NDA accepted via edge function', {
        meta: {
          companyId,
          ndaVersion: nda.version,
          acceptanceId: data.acceptanceId,
        },
      });

      toast.success('NDA accepted successfully');

      // Redirect to dashboard
      navigate('/dashboard');
    } catch (error: any) {
      console.error('Error accepting NDA:', error);
      logger.error('NDA acceptance failed', error, { meta: { companyId } });

      // Handle rate limiting
      if (error.message?.includes('Too many attempts')) {
        toast.error('Too many attempts. Please try again in a few minutes.');
      } else {
        toast.error(error.message || 'Failed to accept NDA. Please try again.');
      }
    } finally {
      setAccepting(false);
    }
  };

  const handleDownloadPdf = async () => {
    try {
      toast.info('Generating PDF...');

      const { data, error } = await supabase.functions.invoke('nda-generate-pdf', {
        body: {
          ndaId: nda.id,
          companyId,
        },
      });

      if (error) throw error;

      // Open PDF in new window for printing
      const printWindow = window.open('', '_blank');
      if (printWindow) {
        printWindow.document.write(data);
        printWindow.document.close();

        // Trigger print dialog after content loads
        printWindow.onload = () => {
          printWindow.print();
        };
      }

      toast.success('PDF ready for download/print');
    } catch (error: any) {
      console.error('Error generating PDF:', error);
      toast.error('Failed to generate PDF');
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="w-full max-w-4xl max-h-[90vh] flex flex-col">
        <CardHeader className="border-b space-y-4">
          <div className="flex justify-center">
            <StaticPyramidLogo />
          </div>

          <div className="flex items-center justify-between gap-4">
            <CardTitle className="text-2xl font-semibold text-foreground">{nda.name}</CardTitle>

            {progressInfo && progressInfo.total > 1 && (
              <Badge variant="secondary" className="shrink-0">
                {progressInfo.current} of {progressInfo.total} NDAs
              </Badge>
            )}
          </div>

          <div className="flex items-center justify-between gap-4">
            <p className="text-sm text-muted-foreground">
              Version {nda.version} • {companyName && `${companyName} • `}Please review and accept
              before continuing
            </p>

            {sessionState.showWarning && sessionState.expiresIn && (
              <Badge variant="destructive" className="shrink-0 flex items-center gap-1">
                <Clock className="h-3 w-3" />
                {Math.ceil(sessionState.expiresIn / 60)}m left
              </Badge>
            )}
          </div>
        </CardHeader>

        <CardContent className="flex-1 overflow-hidden p-0">
          <ScrollArea className="h-[50vh] px-6 py-4">
            <div className="prose prose-sm max-w-none dark:prose-invert">
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                  h1: ({ node, ...props }) => (
                    <h1 className="text-2xl font-bold text-foreground mb-4" {...props} />
                  ),
                  h2: ({ node, ...props }) => (
                    <h2 className="text-xl font-semibold text-foreground mt-6 mb-3" {...props} />
                  ),
                  h3: ({ node, ...props }) => (
                    <h3 className="text-lg font-semibold text-foreground mt-4 mb-2" {...props} />
                  ),
                  p: ({ node, ...props }) => (
                    <p className="text-foreground mb-3 leading-relaxed" {...props} />
                  ),
                  ul: ({ node, ...props }) => (
                    <ul
                      className="list-disc list-inside space-y-1 text-foreground mb-3"
                      {...props}
                    />
                  ),
                  li: ({ node, ...props }) => <li className="text-foreground ml-4" {...props} />,
                  strong: ({ node, ...props }) => (
                    <strong className="font-semibold text-foreground" {...props} />
                  ),
                }}
              >
                {nda.content_md}
              </ReactMarkdown>
            </div>
          </ScrollArea>
        </CardContent>

        <CardFooter className="flex flex-col gap-4 border-t pt-6">
          <div className="flex items-center space-x-2">
            <Checkbox
              id="nda-accept"
              checked={agreed}
              onCheckedChange={(checked) => setAgreed(checked === true)}
              aria-label="Accept Non-Disclosure Agreement"
            />
            <label
              htmlFor="nda-accept"
              className="text-sm font-medium leading-none cursor-pointer peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
            >
              I have read and agree to the Non-Disclosure Agreement
            </label>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 w-full">
            <Button onClick={handleAccept} disabled={!agreed || accepting} className="flex-1">
              {accepting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Processing...
                </>
              ) : (
                'Accept & Continue'
              )}
            </Button>

            <Button variant="outline" onClick={handleDownloadPdf} className="sm:w-auto">
              <FileDown className="mr-2 h-4 w-4" />
              Download PDF
            </Button>
          </div>

          <p className="text-xs text-muted-foreground text-center">
            This agreement is required to access company data on CapLedger. Your acceptance is
            logged for audit purposes.
          </p>
        </CardFooter>
      </Card>
    </div>
  );
}
